import React, { useEffect, useState } from 'react';
import cn from '@/utils/cn';

// COMPONENTS
import DateRangeInput from '@/components/DateRangeInput';
import Capsule from '@/components/Capsule';
import { TCheckBoxLabel } from '@/components/Checkbox';
import Button from '@/components/Button';
import { ExportIcon, AdjustmentsVertical } from '@/components/Icons';

// HOME PAGE SPECIFIC HOOKS/TYPES/UTILS
import useHomeContext from '@/hooks/useHomeContext';
import { transactionTypes } from '../data/transactionTypes';
import { TWalletData } from '../types/WalletData';
import { TDateOption, useDateFilter } from '../hooks/useDateFilter';
import FilterComponent, { filtersName } from './FilterComponent';
import { TFilters } from '../types/Filter';
import useAuth from '@/hooks/useAuth';
import { AccountTypeEnum } from '@/context/types/Account';
import { TransactionTypeEnum } from '@/services/types/TransactionData';

const LeftSideClassName = cn(['flex', 'flex-2', 'items-start', 'gap-x-2']);
const RightSideClassName = cn(['flex', 'flex-1', 'justify-end', 'gap-x-2']);

type TDate = Date | null;

interface FiltersProps {
  onOpenExport: () => void;
  onSelectBankAccount: (selected?: TWalletData) => void;
  alwaysVisible?: boolean;
}

const Filters: React.FC<FiltersProps> = ({ onOpenExport, onSelectBankAccount, alwaysVisible }) => {
  const { accountType } = useAuth()!;
  const {
    setDateFilter,
    allAccounts,
    accountsFilter,
    setAccountsFilter,
    teamMemberFilter,
    setTeamMemberFilter,
    transactionFilter,
    setTransactionFilter,
    wallets,
    teamMembers
  } = useHomeContext()!;
  const {
    resetDateDefault,
    rangeName,
    setRangeName,
    startDate,
    setStartOfRange,
    endDate,
    setEndOfRange
  } = useDateFilter();
  const [filters, setFilters] = useState<TFilters>({
    transactionsTypes: [],
    teamMembers: [],
    wallets: [],
    dates: []
  });
  const [focusFilter, setFocusFilter] = useState<string>();
  const [showFilters, setShowFilters] = useState(false);

  // force change checkbox selected options
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      wallets: accountsFilter ?? []
    }));
  }, [accountsFilter]);

  const handleCheckboxFilterChange = (options: {
    option: TCheckBoxLabel;
    isChecked: boolean;
    filterName: keyof typeof filters;
  }) => {
    const { option, isChecked, filterName } = options;
    if (!isChecked) {
      setFilters((prev) => ({
        ...prev,
        [filterName]: prev[filterName].filter((item) => item !== option.value)
      }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [filterName]: [...filters[filterName], option.value]
    }));
  };

  const handleCheckboxSelectAll = (filterName: keyof TFilters, options: string[]) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: options
    }));
  };

  const handleCheckboxUnselectAll = (filterName: keyof TFilters) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: []
    }));
  };

  const handleDateChange = (
    startDate: TDate,
    endDate: TDate,
    rangeName: TDateOption,
    isDefault: boolean
  ) => {
    setDateFilter({ startDate, endDate, rangeName });
    setFilters({
      ...filters,
      dates: isDefault ? [] : [startDate?.toString() ?? ' ', endDate?.toString() ?? ' ']
    });
  };

  const handleWalletFilterApply = () => {
    if (filters.wallets.length == 1) {
      const bankAccountId = filters.wallets[0];
      const found = wallets.find((el) => el.id === bankAccountId);
      if (found) {
        onSelectBankAccount(found);
      } else {
        onSelectBankAccount();
      }
    } else {
      onSelectBankAccount();
    }

    setAccountsFilter(filters.wallets);
  };

  const handleTeamMemberFilterApply = () => {
    setTeamMemberFilter(filters.teamMembers);
  };

  const handleTransactionTypeFilterApply = () => {
    setTransactionFilter(filters.transactionsTypes);
  };

  const handleDateReset = () => {
    resetDateDefault();
    setFilters((prev) => ({
      ...prev,
      dates: []
    }));
  };

  const handleAccountsFilterClear = () => {
    setAccountsFilter([]);
    const getAccountsBalance = (wallets: TWalletData[]) =>
      Number(
        wallets.reduce((prev, curr) => {
          return prev + Number(curr.balance);
        }, 0)
      );

    onSelectBankAccount({
      ...allAccounts,
      balance: getAccountsBalance(wallets)
    });
    setFilters((prev) => ({
      ...prev,
      wallets: []
    }));
  };

  const handleTeamMembersFilterClear = () => {
    setTeamMemberFilter([]);
    setFilters((prev) => ({
      ...prev,
      teamMembers: []
    }));
  };

  const handleTransactionTypesFilterClear = () => {
    setTransactionFilter([]);
    setFilters((prev) => ({
      ...prev,
      transactionsTypes: []
    }));
  };

  const handleForceClose = () => {
    setFocusFilter(undefined);
  };

  const handleFilterFocus = (id: string) => {
    if (focusFilter === id) {
      setFocusFilter(undefined);
    } else {
      setFocusFilter(id);
    }
  };

  const handleFilterDrawerToggle = () => {
    setShowFilters((prev) => {
      const nextValue = !prev;

      if (nextValue == false && focusFilter && filtersName.includes(focusFilter)) {
        setFocusFilter(undefined);
      }

      return nextValue;
    });
  };

  const transactionTypeFilterOptions =
    accountType === AccountTypeEnum.PERSONAL
      ? transactionTypes.filter((type) => type.transactionType !== TransactionTypeEnum.EmergePay)
      : transactionTypes;

  return (
    <div className={cn(alwaysVisible ? 'relative' : 'tablet:hidden mobile:hidden relative')}>
      <div className="flex">
        <div className={cn(LeftSideClassName, 'gap-x-2 flex-wrap')}>
          <div>
            <DateRangeInput
              onForceClose={handleForceClose}
              onClick={() => handleFilterFocus('date-range-filter')}
              dropdownValue={rangeName}
              onStartDateChange={(date) => setStartOfRange(date)}
              onEndDateChange={(date) => setEndOfRange(date)}
              onDropdownChange={(option) => setRangeName(option)}
              startDate={startDate}
              endDate={endDate}
              onDatesApplied={handleDateChange}
              onResetOptions={handleDateReset}
              focusName="date-range-filter"
              show={focusFilter === 'date-range-filter'}
            />
          </div>

          <div>
            <Button variant="secondary" onClick={handleFilterDrawerToggle}>
              <AdjustmentsVertical /> Filter
            </Button>
          </div>
          <Capsule
            variant="input"
            className={cn(!accountsFilter.length ? 'hidden' : '')}
            onClose={handleAccountsFilterClear}
            customLabel={`${accountsFilter.length} Accounts`}
          />
          <Capsule
            variant="input"
            className={cn(!teamMemberFilter.length ? 'hidden' : '')}
            onClose={handleTeamMembersFilterClear}
            customLabel={`${teamMemberFilter.length} Team members`}
          />
          <Capsule
            variant="input"
            className={cn(!transactionFilter.length ? 'hidden' : '')}
            onClose={handleTransactionTypesFilterClear}
            customLabel={`${transactionFilter.length} Transaction types`}
          />
        </div>
        <div className={RightSideClassName}>
          <Button onClick={onOpenExport}>
            <ExportIcon />
            Export All
          </Button>
        </div>
      </div>
      <div className={cn('absolute z-20 mt-2', showFilters ? 'block' : 'hidden')}>
        <div className="flex pt-3">
          <div className={LeftSideClassName}>
            <FilterComponent
              onForceClose={handleForceClose}
              focusFilter={focusFilter}
              onFocusFilter={handleFilterFocus}
              onCheckboxChange={handleCheckboxFilterChange}
              onSelectAll={handleCheckboxSelectAll}
              onUnselectAll={handleCheckboxUnselectAll}
              options={{
                wallets,
                teamMembers,
                transactionTypes: transactionTypeFilterOptions
              }}
              checkedOptions={{
                wallets: filters.wallets,
                teamMembers: filters.teamMembers,
                transactionsTypes: filters.transactionsTypes
              }}
              onApplyWalletFilter={handleWalletFilterApply}
              onApplyTeamMemberFilter={handleTeamMemberFilterApply}
              onApplyTransactionTypeFilter={handleTransactionTypeFilterApply}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
